import { theme } from '@procurify/ui-theme'

import { FilePreviewEmptyStateContainerStyled } from './styles/FilePreviewEmptyStateContainerStyled'
import { FilePreviewStyled } from './styles/FilePreviewStyled'
import { SupportedFileTypes } from './types'
import { useFilePreview } from './useFilePreview'
import { ImageViewer } from '../ImageViewer'
import { PdfViewer } from '../PdfViewer'
import { SecondaryButton } from '@ui/elements/Button'
import { Card } from '@ui/elements/Card'
import { Flex } from '@ui/elements/Flex'
import { Header } from '@ui/elements/Header'
import { IconDownload } from '@ui/elements/Icon'
import { IconFile } from '@ui/elements/Icon/components/IconFile'
import { CircleLoader } from '@ui/elements/Loaders'
import { Space } from '@ui/elements/Space'
import { SubText } from '@ui/elements/Text'

export interface IFilePreviewProps {
  src: string
  filename?: string
  mimetype?: string
  enableDownload?: boolean
  /**
   * Alt text for the image
   */
  alt?: string
  strings?: Partial<typeof DEFAULT_STRINGS>
}

const DEFAULT_STRINGS = {
  previewNotAvailable: 'Preview not available',
  downloadButtonText: 'Download',
}

const PreviewLoader = () => {
  return (
    <Flex.Row
      alignItems='center'
      justifyContent='center'
      height='100%'
      width={1}
      m={0}
    >
      <CircleLoader size={64} color={theme.colors.textPrimary} />
    </Flex.Row>
  )
}

export const FilePreview = ({
  src,
  filename,
  mimetype,
  enableDownload = true,
  alt,
  strings = {},
}: IFilePreviewProps) => {
  const { isLoading, fileType } = useFilePreview({
    src,
    filename,
    mimetype,
  })

  const extendedStrings = { ...DEFAULT_STRINGS, ...strings }

  const renderPreview = () => {
    switch (fileType) {
      case 'pdf':
        return <PdfViewer data={src} height='100%' width='100%' />
      case 'image':
        return (
          <ImageViewer image={src} alt={alt || `Image - ${filename || src}`} />
        )
      default:
        return renderEmptyPreview()
    }
  }

  const getClassName = () => {
    if (fileType === SupportedFileTypes.PDF) {
      return 'filetype-pdf'
    } else if (fileType === SupportedFileTypes.IMAGE) {
      return 'filetype-image'
    } else {
      return 'filetype-empty'
    }
  }

  const canDownloadFile = enableDownload && src

  const renderEmptyPreview = () => (
    <FilePreviewEmptyStateContainerStyled>
      <Card>
        <Card.Content>
          <Space>
            <Space size='small'>
              <IconFile size={64} />
              {filename && <SubText breakWord>{filename}</SubText>}
            </Space>
            <Header as='h5'>{extendedStrings.previewNotAvailable}</Header>
            {canDownloadFile && (
              <a href={src} download>
                <SecondaryButton iconComponent={<IconDownload />}>
                  {extendedStrings.downloadButtonText}
                </SecondaryButton>
              </a>
            )}
          </Space>
        </Card.Content>
      </Card>
    </FilePreviewEmptyStateContainerStyled>
  )

  const showLoader = isLoading
  const showPreview = !isLoading && !!src
  const showEmptyPreview = !isLoading && !src

  return (
    <FilePreviewStyled className={`${getClassName()}`}>
      {showLoader && <PreviewLoader />}
      {showPreview && renderPreview()}
      {showEmptyPreview && renderEmptyPreview()}
    </FilePreviewStyled>
  )
}
